import React from "react";
import { AlertCircle } from "lucide-react";
import { AppButton, AppCard, AppCardContent, Callout, EmptyState } from "../../../ui";
import Shimmer from "../../../Common/Shimmer";
import StudentPortfolioSections from "../../../Portfolio/StudentPortfolioSections";
import { ScorePill } from "../../../Portfolio/ResumeReport";
import { errorText } from "../../../Portfolio/portfolioApi";
import { DASH, formatDate, orDash } from "../../../../utils/format";
import RecordCardHead from "../RecordCardHead";
import RecordStat from "../RecordStat";

/**
 * PortfolioTab — the student's own portfolio, read-only, inside their record.
 *
 * The sections are the ones the student edits (`StudentPortfolioSections`), so
 * staff read exactly what the student wrote. The summary above them is the
 * count a placement officer scans before deciding whether to read on.
 */
const PortfolioTab = ({ studentId, portfolio }) => {
  if (portfolio.loading && !portfolio.data) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Shimmer height={120} />
        <Shimmer height={260} />
      </div>
    );
  }

  if (portfolio.error && !portfolio.data) {
    const status = portfolio.error?.response?.status;
    // 403 / 404 are an access answer, not a fault — say it plainly.
    return status === 403 || status === 404 ? (
      <EmptyState
        icon={AlertCircle}
        title="Portfolio not available"
        description={errorText(portfolio.error, "You do not have access to this student's portfolio.")}
      />
    ) : (
      <Callout
        tone="danger"
        role="alert"
        action={<AppButton size="xs" variant="outline" onClick={portfolio.reload}>Try again</AppButton>}
      >
        {errorText(portfolio.error, "Failed to load the portfolio.")}
      </Callout>
    );
  }

  const p = portfolio.data?.portfolio;
  const earned = portfolio.data?.earned;
  const analysis = portfolio.data?.latestAnalysis;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {p && (
        <AppCard>
          <RecordCardHead
            title="Portfolio at a glance"
            description={`Built by the student and only editable by them.${p.updatedAt ? ` Last updated ${formatDate(p.updatedAt)}.` : ""}`}
          />
          <AppCardContent>
            <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-5 md:tw-grid-cols-3 min-[1200px]:tw-grid-cols-6">
              <RecordStat label="Target role" value={orDash(p.careerProfile?.targetRole)} />
              <RecordStat
                label="Resume score"
                value={analysis?.status === "completed" ? <ScorePill score={analysis.overallScore} size="sm" /> : DASH}
                hint={p.resume?.key ? undefined : "No resume uploaded"}
              />
              <RecordStat label="Skills" value={p.careerProfile?.skills?.length || 0} />
              <RecordStat label="Projects" value={p.projects?.length || 0} />
              <RecordStat
                label="Certifications"
                value={(p.certifications?.length || 0) + (earned?.certificates?.length || 0)}
                hint={earned?.certificates?.length ? `${earned.certificates.length} issued here` : undefined}
              />
              <RecordStat label="Achievements" value={p.achievements?.length || 0} />
            </dl>
          </AppCardContent>
        </AppCard>
      )}

      <StudentPortfolioSections studentId={studentId} data={portfolio.data} />
    </div>
  );
};

export default PortfolioTab;
